import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { Calendar, Heart, User, Users, AlertCircle, CheckCircle, ChevronDown } from "lucide-react";


const MaritalForm = ({ formData, preview, setPreview, handleChange, loading, errors, onSave }) => {
    const [maritalStatus, setMaritalStatus] = useState("");
    const [localErrors, setLocalErrors] = useState({});


    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!maritalStatus) {
            newErrors.maritalStatus = "Please select your marital status";
        }
        if (maritalStatus === "married" && !formData?.dateOfMarriage) {
            newErrors.dateOfMarriage = "Date of marriage is required";
        }
        if (["divorced", "separated", "widow", "widower"].includes(maritalStatus) && !formData?.separationOrDeathDate) {
            newErrors.separationOrDeathDate = "Date of separation/death is required";
        }
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    return (
        <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving marital information...</p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-5 sm:px-6 md:px-8 py-5 sm:py-6 text-white">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                                <Users size={22} className="text-blue-300 sm:w-[24px] sm:h-[24px]" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl md:text-2xl font-bold">Marital Status</h2>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Required for visa application processing</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-5 sm:p-6 md:p-8">
                    {/* Error Display */}
                    {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                        <div className="mb-6 flex items-start gap-3 bg-rose-50 border border-rose-100 text-rose-700 p-4 rounded-xl animate-in fade-in slide-in-from-top-2">
                            <AlertCircle className="shrink-0 mt-0.5" size={18} />
                            <div className="text-xs sm:text-sm">
                                <p className="font-bold mb-1">Action Required:</p>
                                <ul className="list-disc ml-4 space-y-0.5">
                                    {Object.values(localErrors).map((err, i) => <li key={i}>{err}</li>)}
                                    {errors?.map((err, i) => <li key={`err-${i}`}>{err}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}

                    {preview ? (
                        /* ================= PREVIEW MODE ================= */
                        <div className="space-y-6 animate-in fade-in duration-300">
                            {/* Status Banner for Approved */}
                            {preview.status === "approve" && (
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                                    <CheckCircle size={20} className="text-green-600 shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-green-800">Marital Status Verified</p>
                                        <p className="text-xs text-green-700">Your information has been verified and approved.</p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4">
                                <PreviewCard
                                    label="Current Status"
                                    value={preview.maritalStatus?.replace(/_/g, " ")}
                                    icon={<User className="text-blue-600" />}
                                    capitalize
                                />
                                {preview.dateOfMarriage && (
                                    <PreviewCard
                                        label="Date of Marriage"
                                        value={formatDate(preview.dateOfMarriage)}
                                        icon={<Heart className="text-rose-500" />}
                                    />
                                )}
                                {preview.separationOrDeathDate && (
                                    <PreviewCard
                                        label="Separation / Death Date"
                                        value={formatDate(preview.separationOrDeathDate)}
                                        icon={<Calendar className="text-blue-500" />}
                                    />
                                )}
                            </div>


                        </div>
                    ) : (
                        /* ================= FORM MODE ================= */
                        <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-500">
                            {/* Marital Status Select */}
                            <div className="space-y-2">
                                <label className="text-xs sm:text-sm font-bold text-gray-700 ml-1 flex items-center gap-1">
                                    Marital Status <span className="text-rose-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        value={maritalStatus}
                                        onChange={(e) => setMaritalStatus(e.target.value)}
                                        className={`w-full appearance-none bg-gray-50 border rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3 focus:border-blue-500 focus:bg-white outline-none transition-all cursor-pointer text-sm sm:text-base text-gray-800
                                            ${localErrors.maritalStatus ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                    >
                                        <option value="">Select your marital status</option>
                                        <option value="single">Single</option>
                                        <option value="married">Married</option>
                                        <option value="common_law">Common Law</option>
                                        <option value="divorced">Divorced</option>
                                        <option value="separated">Separated</option>
                                        <option value="widow">Widow / Widower</option>
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {localErrors.maritalStatus && (
                                    <p className="text-rose-500 text-[10px] sm:text-xs mt-1 flex items-center gap-1">
                                        <AlertCircle size={12} /> {localErrors.maritalStatus}
                                    </p>
                                )}
                            </div>

                            {/* Date of Marriage (shown when married) */}
                            {maritalStatus === "married" && (
                                <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                    <label className="text-xs sm:text-sm font-bold text-gray-700 ml-1 flex items-center gap-1">
                                        Date of Marriage <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData?.dateOfMarriage || ""}
                                        onChange={(e) => handleChange("dateOfMarriage", e.target.value)}
                                        max={new Date().toISOString().split("T")[0]}
                                        className={`w-full bg-gray-50 border rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                            ${localErrors.dateOfMarriage ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                    />
                                    {localErrors.dateOfMarriage && (
                                        <p className="text-rose-500 text-[10px] sm:text-xs mt-1">{localErrors.dateOfMarriage}</p>
                                    )}
                                </div>
                            )}

                            {/* Separation/Death Date (shown for divorced, separated, widow/widower) */}
                            {["divorced", "separated", "widow", "widower"].includes(maritalStatus) && (
                                <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                    <label className="text-xs sm:text-sm font-bold text-gray-700 ml-1 flex items-center gap-1">
                                        {maritalStatus === "widow" ? "Date of Death" : "Date of Separation"} <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData?.separationOrDeathDate || ""}
                                        onChange={(e) => handleChange("separationOrDeathDate", e.target.value)}
                                        max={new Date().toISOString().split("T")[0]}
                                        className={`w-full bg-gray-50 border rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                            ${localErrors.separationOrDeathDate ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                    />
                                    {localErrors.separationOrDeathDate && (
                                        <p className="text-rose-500 text-[10px] sm:text-xs mt-1">{localErrors.separationOrDeathDate}</p>
                                    )}
                                </div>
                            )}

                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

// Preview Card Component
const PreviewCard = ({ label, value, icon, capitalize }) => (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-blue-100 hover:bg-white transition-all duration-200">
        <div className="p-2.5 sm:p-3 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 shrink-0">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">
                {label}
            </p>
            <p className={`text-sm sm:text-base text-gray-800 font-bold truncate ${capitalize ? 'capitalize' : ''}`}>
                {value || "Not Specified"}
            </p>
        </div>
    </div>
);

export default MaritalForm;